import { z } from 'zod';
import { EmbeddingVectorSchema } from './evidence-embedding.js';

/**
 * Jarvis Knowledge contracts (B5 / IDEA-80 — ADR 0090).
 *
 * The operator uploads their own TEXT / MARKDOWN documents to a private, owner-scoped knowledge
 * base; the gateway chunks + embeds each into pgvector (reusing the ADR 0086 evidence embedding
 * stack verbatim — same Voyage `voyage-3.5-lite` 1024-dim model + `chunkText` + cost ledger), and
 * Jarvis searches it with the gateway-resolved `knowledge.search` tool to ground its answers.
 *
 * TEXT-FIRST (load-bearing, ADR 0090 §1): the browser reads `file.text()` and posts the text in a
 * capped JSON body — NO binary handling, NO PDF/docx parser, NO new dependency. These are the
 * boundary shapes for that data (parse, don't validate — ADR 0005). The vector is a plain
 * `number[]` on the wire; pgvector's `vector(1024)` is a storage detail the gateway maps to/from it.
 */

/** Bump when the persisted knowledge-row shape changes (parse-on-read gate). */
export const KNOWLEDGE_SCHEMA_VERSION = 1 as const;

/**
 * Max characters of uploaded document text (TEXT-first cap, ADR 0090 §1). Bounds the ingest
 * fan-out (a doc chunks to ≲150 chunks at this ceiling) and rides under the route's byte cap.
 */
export const KNOWLEDGE_TEXT_MAX_CHARS = 256 * 1024;

/** Max chars in one knowledge chunk's text — matches the evidence chunk ceiling. */
export const KNOWLEDGE_CHUNK_MAX_CHARS = 8_000;

/** Bound on an operator-supplied document title. */
export const KNOWLEDGE_TITLE_MAX_CHARS = 200;

/**
 * Hard cap on chunks embedded per document (bounded fan-out, ADR 0090 §5) — a safety net above
 * the byte-cap-implied maximum (a 256 KiB text chunks to ≲150 units). A document that exceeds it
 * is rejected with a visible error rather than silently truncated.
 */
export const KNOWLEDGE_MAX_CHUNKS_PER_DOC = 256;

/** The only document source B5 accepts (binary PDF/docx is deferred — ADR 0090 §1, backlog N-PDF). */
export const KnowledgeDocumentSourceSchema = z.literal('text');
export type KnowledgeDocumentSource = z.infer<typeof KnowledgeDocumentSourceSchema>;

/**
 * Ingest lifecycle of a document: `pending` on upload, `ready` once its chunks are embedded +
 * stored, `error` when ingest failed (the row carries the classified reason — no silent failure).
 */
export const KnowledgeDocumentStatusSchema = z.enum(['pending', 'ready', 'error']);
export type KnowledgeDocumentStatus = z.infer<typeof KnowledgeDocumentStatusSchema>;

/** A persisted knowledge document header (owner-scoped; the chunks live in `knowledge_chunks`). */
export const KnowledgeDocumentSchema = z.object({
  id: z.string().min(1).max(64),
  ownerId: z.string().min(1).max(64),
  title: z.string().min(1).max(KNOWLEDGE_TITLE_MAX_CHARS),
  source: KnowledgeDocumentSourceSchema,
  /** Character length of the ingested text (telemetry + UI). */
  charCount: z.number().int().nonnegative(),
  /** Chunks embedded for this document (0 until ingest completes). */
  chunkCount: z.number().int().nonnegative(),
  status: KnowledgeDocumentStatusSchema,
  /** Present only when `status === 'error'`: a short, human-readable reason (never a secret). */
  error: z.string().min(1).max(500).optional(),
  /** Epoch ms the row was created (injected clock — ADR 0002). */
  createdAt: z.number().finite(),
});
export type KnowledgeDocument = z.infer<typeof KnowledgeDocumentSchema>;

/**
 * A persisted knowledge chunk + its embedding vector (owner-scoped, cascade-linked to its
 * document). Mirrors `EvidenceEmbeddingRowSchema` — same `model`+`dimensions` lineage, same
 * `vector(N)` storage detail on the wire as a plain array.
 */
export const KnowledgeChunkRowSchema = z.object({
  id: z.string().min(1).max(64),
  ownerId: z.string().min(1).max(64),
  documentId: z.string().min(1).max(64),
  chunkIndex: z.number().int().nonnegative(),
  content: z.string().min(1).max(KNOWLEDGE_CHUNK_MAX_CHARS),
  tokenCount: z.number().int().nonnegative().optional(),
  embedding: EmbeddingVectorSchema,
  dimensions: z.number().int().positive(),
  model: z.string().min(1).max(120),
  schemaVersion: z.literal(KNOWLEDGE_SCHEMA_VERSION),
  createdAt: z.number().finite(),
});
export type KnowledgeChunkRow = z.infer<typeof KnowledgeChunkRowSchema>;

/**
 * One nearest-neighbour hit from a knowledge similarity query — the matched chunk plus its cosine
 * similarity (1 = identical direction) and the document it came from. The vector itself is not
 * returned (only what Jarvis needs to ground an answer: which document, which passage, how close).
 */
export const KnowledgeChunkMatchSchema = z.object({
  documentId: z.string().min(1).max(64),
  chunkIndex: z.number().int().nonnegative(),
  content: z.string().min(1).max(KNOWLEDGE_CHUNK_MAX_CHARS),
  /** Cosine similarity in [0,1]; higher is closer. */
  similarity: z.number().finite(),
});
export type KnowledgeChunkMatch = z.infer<typeof KnowledgeChunkMatchSchema>;
